/**
 * Canvases home — the default landing surface (rail mode "dashboard"). Renders
 * the selected canvas (?cv=<slug>) TENANT-WIDE, full-width, via the shared
 * CanvasItems renderer. In the "hub" layout it doubles as the single front
 * door: the same main column hosts the docked chat (full width) when a chat is
 * the active view (?chat=1). The canvas is chosen from the top tab strip or
 * the sidebar; the chat from the sidebar's history list.
 *
 * Chat reuses the existing dock protocol (dockStore) — the ONE ChatConsole
 * instance portals into our host. We keep the host mounted across canvas↔chat
 * (just hidden under the canvas in canvas view) so the chat keeps running and
 * its published chatBar — which feeds the sidebar history — stays live.
 *
 * Intentionally unscoped; a value click jumps into Explore (HOME_VALUE_CLICK).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ScrollArea from "../components/ScrollArea.jsx";
import CanvasItems from "../components/lenses/CanvasItems.jsx";
import { normalizeRows } from "../lib/canvasLayout.js";
import { getCurrentTenantIdSync } from "../lib/userInfo.js";
import EmptyState, { ConnectEndpointsAction, useTenantEmpty } from "../components/EmptyState.jsx";
import {
    kernelResolverForTenant,
    listKernels,
    subscribeKernels,
} from "../lib/kernelStore.js";
import {
    bundledCanvasRunConcurrency,
    isBundledCanvasDefinition,
    isStoredCanvasDefinition,
} from "../lib/canvasTemplateStore.js";
import {
    isStoredTenantViewBody,
    loadTenantViewBody,
} from "../lib/tenantViewCloudStore.js";
import {
    recordTenantViewOpen,
    recordTenantViewRun,
} from "../lib/tenantViewsApi.js";
import { getDockBodyHost, setDockBodyHost, setDockAvailable, setDockColumnExpanded, setDockHostPage } from "../lib/dockStore.js";
import { useTraceCanvasOpen } from "../lib/traceCanvasStore.js";
import {
    INVOCATION_SOURCES,
    RUN_SURFACES,
    createBundledRunContext,
    createStoredRunContext,
    createUnknownRunContext,
    isLegacyLocalCanvas,
    legacyCommandCanvasCellAdapter,
} from "../lib/kernelResolver.js";

// What clicking a value inside a widget does:
//   "explore" → jump into the Explore page scoped to that value (#4).
//   "off"     → read-only dashboard; value clicks do nothing (#1).
const HOME_VALUE_CLICK = "explore";

// Vertical-only scroll — x:hidden keeps a self-measuring widget from pushing
// the column wider (same guard as CanvasesBody).
const SCROLL_OPTS = Object.freeze({ overflow: { x: "hidden", y: "scroll" } });

function NoCanvas() {
    return (
        <EmptyState
            title="No canvas selected"
            note="Pick a canvas from the tabs above, or the list on the left."
        />
    );
}

export default function CanvasHomePage({ canvases = [], selectedSlug, range, onOpenInExplore, layout = "hub", chatActive = false }) {
    const tenantId = getCurrentTenantIdSync();

    // Kernels kept live so a fork/save shows up without a reload.
    const [kernels, setKernels] = useState(() => listKernels(tenantId));
    useEffect(() => subscribeKernels(() => setKernels(listKernels(tenantId))), [tenantId]);
    const kernelResolver = useMemo(
        () => kernelResolverForTenant(tenantId),
        [kernels, tenantId],
    );

    const active = useMemo(
        () => canvases.find((c) => c.slug === selectedSlug) || null,
        [canvases, selectedSlug],
    );
    const [bodyLoadError, setBodyLoadError] = useState(null);
    useEffect(() => {
        setBodyLoadError(null);
        if (!active?.tenantViewId || active.bodyLoaded === true) return undefined;
        const controller = new AbortController();
        loadTenantViewBody(active.tenantViewId, {
            tenantId,
            signal: controller.signal,
            withDependencies: true,
        }).catch((error) => {
            if (error?.name !== "AbortError") setBodyLoadError(error);
        });
        return () => controller.abort();
    }, [active?.bodyLoaded, active?.tenantViewId, tenantId]);
    const isLegacyCanvas = isLegacyLocalCanvas(active);
    const legacyCellAdapter = isLegacyCanvas
        ? legacyCommandCanvasCellAdapter
        : null;
    const invocationSource = !active || isLegacyCanvas
        ? INVOCATION_SOURCES.UNKNOWN
        : isBundledCanvasDefinition(active)
            ? INVOCATION_SOURCES.BUNDLED
            : isStoredCanvasDefinition(active)
                ? INVOCATION_SOURCES.STORED
                : INVOCATION_SOURCES.UNKNOWN;
    const runContext = useMemo(
        () => {
            const metadata = { surface: RUN_SURFACES.CANVAS };
            if (invocationSource === INVOCATION_SOURCES.BUNDLED) {
                return createBundledRunContext(metadata);
            }
            if (invocationSource === INVOCATION_SOURCES.STORED) {
                return createStoredRunContext(metadata);
            }
            return createUnknownRunContext(metadata);
        },
        [invocationSource],
    );
    const storedActive = isStoredTenantViewBody(active) ? active : null;
    const canvasVisible = layout !== "hub" || !chatActive;
    // Once the canvas has been shown, keep it mounted. Switching to the docked
    // chat used to unmount it, which discarded every widget's result, so coming
    // back re-ran the whole canvas and read as a reload. The chat host beside it
    // already stays mounted for the same class of reason. Latched rather than
    // always-on: the param-less front door opens on the chat, and mounting
    // there would run a canvas nobody is looking at.
    const [canvasMounted, setCanvasMounted] = useState(canvasVisible);
    useEffect(() => {
        if (canvasVisible) setCanvasMounted(true);
    }, [canvasVisible]);
    const reportedOpenIdentity = useRef(null);
    useEffect(() => {
        if (!canvasVisible) {
            reportedOpenIdentity.current = null;
            return;
        }
        if (reportedOpenIdentity.current === storedActive) return;
        reportedOpenIdentity.current = storedActive;
        if (!storedActive?.tenantViewId) return;
        void recordTenantViewOpen(storedActive.tenantViewId).catch((error) => {
            console.warn("[canvas-home] open telemetry failed", error);
        });
    }, [canvasVisible, storedActive]);
    const reportRunOutcome = useCallback((event) => {
        if (!storedActive?.tenantViewId) return;
        void recordTenantViewRun(storedActive.tenantViewId, {
            outcome: event.outcome,
            reasonCategory: event.reasonCategory,
            analyticsQueryCount: event.analyticsQueryCount ?? 0,
        }).catch((error) => {
            console.warn("[canvas-home] run telemetry failed", error);
        });
    }, [storedActive]);
    const activeIdentityKey = active
        ? `${active.tenantViewId || active.id || active.slug}:${active.version ?? ""}`
        : "no-canvas";
    const runConcurrency = bundledCanvasRunConcurrency(active);

    // Always tenant-wide: scope is []. Token kernels run global everywhere;
    // scoped kernels read the whole tenant at the root.
    const kernelParams = useMemo(
        () => ({ scope: [], range: range || null, query: "" }),
        [range],
    );

    // Value click → jump into Explore scoped to the clicked entity (#4), or a
    // no-op for read-only mode (#1). A no-op (not undefined) is required:
    // KernelWidget's default handler opens the scope in a fresh tab.
    const onOpenScope = useMemo(() => {
        if (HOME_VALUE_CLICK === "off") return () => {};
        return ({ dim, ids }) => {
            if (!dim || !ids?.length) return;
            onOpenInExplore?.([{ dim, ids, id: ids[0] }]);
        };
    }, [onOpenInExplore]);

    const hub = layout === "hub";

    // On a tenant with no endpoints ever reporting, every widget below reads
    // zeros — say why ONCE at canvas level instead of letting each widget
    // shrug "no data". Reached via ?cv= deep links and tab clicks; the
    // param-less landing routes to the Connect surface before this renders.
    const tenantEmpty = useTenantEmpty({ enabled: Boolean(active) && !chatActive });

    // Hub mode: claim the shared dock. dockAvailable + a body host = "docked",
    // which makes the single ChatConsole portal into our host AND publish its
    // chatBar (the sidebar's chat history feeds off it). Stable ref → set once
    // on mount, cleared on unmount; the host div stays mounted across
    // canvas↔chat so the chat never tears down.
    // A canvas of traces over the page docks the chat in its own dock while it
    // is up; the home page takes it back when it closes.
    const canvasUp = !!useTraceCanvasOpen();
    const dockHostEl = useRef(null);
    const dockHostRef = useCallback((el) => {
        if (el) {
            dockHostEl.current = el;
            if (hub) setDockBodyHost(el);
            return;
        }
        // Gone: let go of the host only if it is still this page's.
        if (dockHostEl.current && getDockBodyHost() === dockHostEl.current) setDockBodyHost(null);
        dockHostEl.current = null;
    }, [hub]);
    useEffect(() => {
        if (!hub || canvasUp) return undefined;
        setDockAvailable(true);
        setDockHostPage(true);
        setDockColumnExpanded(true);
        if (dockHostEl.current) setDockBodyHost(dockHostEl.current);
        return () => { setDockAvailable(false); setDockHostPage(false); setDockColumnExpanded(false); };
    }, [hub, canvasUp]);

    const canvasBody = (
        <ScrollArea
            data-content-scroll
            className="min-h-0 flex-1"
            contentClassName="pt-[12px] pb-[24px]"
            options={SCROLL_OPTS}
        >
            {active && tenantEmpty ? (
                <EmptyState
                    className="mb-[10px]"
                    title="No endpoints are reporting yet"
                    note="Every widget below reads live tenant data. They fill in once the agent is installed on your endpoints."
                    action={<ConnectEndpointsAction />}
                />
            ) : null}
            {active?.tenantViewId && active.bodyLoaded !== true ? (
                <div
                    className="grid place-items-center px-[12px] py-[24px] text-[12px] text-[color:var(--muted)]"
                    aria-busy={bodyLoadError ? undefined : "true"}
                    role={bodyLoadError ? "alert" : "status"}
                >
                    {bodyLoadError
                        ? `Could not load canvas: ${bodyLoadError.message || bodyLoadError}`
                        : "Loading canvas…"}
                </div>
            ) : active ? (
                <CanvasItems
                    key={activeIdentityKey}
                    rows={normalizeRows(active)}
                    kernelResolver={kernelResolver}
                    legacyCellAdapter={legacyCellAdapter}
                    runContext={runContext}
                    kernelParams={kernelParams}
                    runtime={active.runtime}
                    canvasKey={active.tenantViewId || active.id || active.slug}
                    tenantId={tenantId}
                    runConcurrency={runConcurrency}
                    onOpenScope={onOpenScope}
                    onRunOutcome={reportRunOutcome}
                />
            ) : (
                <NoCanvas />
            )}
        </ScrollArea>
    );

    if (!hub) return <div className="flex h-full min-h-0 min-w-0 flex-col">{canvasBody}</div>;

    return (
        <div className="relative h-full min-h-0 min-w-0">
            {/* Docked-chat host — always mounted AND laid out (visibility, not
                display:none) so the docked chat measures the real column even
                before it's first revealed; otherwise the composer mis-sizes.
                Sits under the canvas, revealed in chat view. */}
            <div
                ref={dockHostRef}
                className="absolute inset-0 flex min-h-0 min-w-0 flex-col"
                style={{ visibility: chatActive ? undefined : "hidden" }}
            />
            {canvasMounted ? (
                <div
                    className="absolute inset-0 flex min-h-0 min-w-0 flex-col bg-[color:var(--bg)]"
                    style={{ visibility: chatActive ? "hidden" : undefined }}
                >
                    {canvasBody}
                </div>
            ) : null}
        </div>
    );
}
